// PrintLayoutEditor.jsx — مصمم الطباعة بالسحب والإفلات
// ضعه مكان الملف القديم (نفس المجلد)، المسارات ../../ كما في ملفك الأصلي
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, MenuItem, IconButton, Tooltip, Button, Divider, Menu, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, Switch, FormControlLabel, Collapse, InputAdornment, CircularProgress,
} from "@mui/material";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import GridOnIcon from "@mui/icons-material/GridOn";
import GridOffIcon from "@mui/icons-material/GridOff";
import StraightenIcon from "@mui/icons-material/Straighten";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import AutoAwesomeMosaicOutlinedIcon from "@mui/icons-material/AutoAwesomeMosaicOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CodeIcon from "@mui/icons-material/Code";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import axios from "../../../axios";
import printLayoutApi from "../../../api/printLayoutApi";
import { ReportHeader } from "../../../components/Reports/ReportKit";
import DesignerCanvas, { DRAG_MIME } from "../../../components/PrintDesigner/DesignerCanvas";
import PropertiesPanel from "../../../components/PrintDesigner/PropertiesPanel";
import PrintLayoutRenderer from "../../../components/PrintDesigner/PrintLayoutRenderer";
import { VARIABLE_GROUPS, MOCK_DATA, MOCK_VOUCHER, variableGroupsFor, customFieldsGroup } from "../../../components/PrintDesigner/printVariables";
import {
  PAPER, contentWidth, makeElement, newId, templateForPaper, templatesFor, templateForPaperOf, isVoucherType, normalizeLayout,
  parseTemplateSet, serializeTemplateSet, defaultOf, tplId, paperLabel,
} from "../../../components/PrintDesigner/printTemplates";

const ACCENT = "#4F46E5";
const HISTORY_LIMIT = 80;

const TOOLS = [
  { type: "text", label: "نص", icon: <TextFieldsIcon /> },
  { type: "logo", label: "الشعار", icon: <BusinessOutlinedIcon /> },
  { type: "image", label: "صورة", icon: <ImageOutlinedIcon /> },
  { type: "qr", label: "QR", icon: <QrCode2Icon /> },
  { type: "line", label: "خط", icon: <HorizontalRuleIcon /> },
  { type: "rect", label: "مستطيل", icon: <CropSquareIcon /> },
  { type: "totals", label: "الإجماليات", icon: <CalculateOutlinedIcon /> },
  { type: "html", label: "HTML", icon: <CodeIcon /> },
];

const panelSx = {
  borderRadius: "14px", border: "1px solid #e5e7eb", bgcolor: "#fff", overflow: "hidden",
  display: "flex", flexDirection: "column", minHeight: 0,
};
const isTyping = (t) => t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);

// يحوّل بيانات /CompanyProfile إلى شكل company المستخدم في الطباعة
const mapCompany = (c) => (!c ? {} : {
  name: c.name || c.companyName || c.arabicName || c.nameAr,
  nameEn: c.nameEn || c.englishName || c.companyNameEn,
  taxNumber: c.taxNumber || c.vatNumber,
  crNumber: c.crNumber || c.commercialRegistration || c.commercialRegister || c.commercialRecord,
  address: c.address || c.addressAr,
  addressEn: c.addressEn || c.englishAddress,
  phone: c.phone || c.mobile,
  email: c.email,
  website: c.website,
  logoUrl: c.logoUrl || c.logo || c.logoBase64,
});
// عنصر نص لحقل بيانات
function fieldElement(band, x, y, v, labelOn) {
  const isTotal = VARIABLE_GROUPS.find(g => g.key === "totals")?.items.some(i => i.key === v.key);
  return makeElement("text", band, x, y, {
    w: labelOn ? 70 : 45, h: 7,
    props: { text: labelOn ? `${v.label}: {${v.key}}` : `{${v.key}}`, fontSize: 11, bold: isTotal },
  });
}

// أنواع السندات — قوالبها تُحفظ برقم النوع (101..105) في نفس جدول تصاميم الطباعة
const VOUCHER_TYPE_LIST = [
  { invoiceTypeId: 101, name: "سند قبض من عميل" },
  { invoiceTypeId: 103, name: "سند قبض عام" },
  { invoiceTypeId: 102, name: "سند صرف لمورد" },
  { invoiceTypeId: 104, name: "سند صرف عام" },
  { invoiceTypeId: 105, name: "سند صرف ضريبة" },
];
// ملف تصدير/استيراد التصاميم (يُنقل بين الأجهزة والشركات)
const FILE_TAG = "profit-print-templates";
const downloadJson = (obj, filename) => {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};
const safeName = (s) => String(s || "").replace(/[\\/:*?"<>|]+/g, "-").trim() || "تصميم";
const stripTpl = (t) => ({ name: t.name, isDefault: !!t.isDefault, layout: { ...t.layout, version: 2 } });

const initialKind = () => (new URLSearchParams(window.location.search).get("kind") === "voucher" ? "voucher" : "invoice");

const pick = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v != null && v !== ""));

export default function PrintLayoutEditor() {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const [kind, setKind] = useState(initialKind); // "invoice" | "voucher"
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  // الحقول المخصصة لنوع الفاتورة المختار (تظهر كمجموعة متغيرات قابلة للسحب)
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [selectedType, setSelectedType] = useState(() => (initialKind() === "voucher" ? VOUCHER_TYPE_LIST[0].invoiceTypeId : ""));
  const types = kind === "voucher" ? VOUCHER_TYPE_LIST : invoiceTypes;
  const TEMPLATES = templatesFor(kind);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState({});

  const [layout, setLayoutState] = useState(() => templateForPaper("A4"));
  const layoutRef = useRef(layout);
  const hist = useRef({ past: [], future: [], gesture: null });
  const [, setHistTick] = useState(0); // لإعادة الرسم عند تغيّر التاريخ
  const savedJson = useRef("");

  // قوالب نوع الفاتورة الحالي (عدة قوالب: A4، حراري، ...) — القالب المفتوح نسخته الحية في layout
  const [tpls, setTpls] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [tplDialog, setTplDialog] = useState(null); // { mode: 'new'|'rename', name, base }

  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(true);
  const [snap, setSnap] = useState(true);
  const [showData, setShowData] = useState(true);
  const [withLabel, setWithLabel] = useState(true);
  const [fieldQuery, setFieldQuery] = useState("");
  const [openGroups, setOpenGroups] = useState(() => Object.fromEntries(VARIABLE_GROUPS.map((g, i) => [g.key, i < 3 || g.key === "voucher"])));
  const [tplAnchor, setTplAnchor] = useState(null);
  const [preview, setPreview] = useState(false);
  const canvasBox = useRef(null);
  const previewRef = useRef(null);

  const currentSet = useMemo(() => tpls.map(t => (t.id === activeId ? { ...t, layout } : t)), [tpls, activeId, layout]);
  const dirty = tpls.length > 0 && serializeTemplateSet(currentSet) !== savedJson.current;

  // ───────── التاريخ (تراجع / إعادة) ─────────
  const apply = useCallback((next) => { layoutRef.current = next; setLayoutState(next); }, []);
  const commit = useCallback((next) => {
    const h = hist.current;
    h.past.push(layoutRef.current);
    if (h.past.length > HISTORY_LIMIT) h.past.shift();
    h.future = [];
    apply(next);
    setHistTick(t => t + 1);
  }, [apply]);
  const reset = useCallback((next) => {
    hist.current = { past: [], future: [], gesture: null };
    apply(next);
    setSelectedId(null); setEditingId(null);
    setHistTick(t => t + 1);
  }, [apply]);
  const undo = useCallback(() => {
    const h = hist.current;
    if (!h.past.length) return;
    h.future.push(layoutRef.current);
    apply(h.past.pop());
    setEditingId(null);
    setHistTick(t => t + 1);
  }, [apply]);
  const redo = useCallback(() => {
    const h = hist.current;
    if (!h.future.length) return;
    h.past.push(layoutRef.current);
    apply(h.future.pop());
    setHistTick(t => t + 1);
  }, [apply]);
  const gestureStart = useCallback(() => { hist.current.gesture = layoutRef.current; }, []);
  const gestureEnd = useCallback(() => {
    const h = hist.current;
    if (h.gesture && h.gesture !== layoutRef.current) {
      h.past.push(h.gesture);
      if (h.past.length > HISTORY_LIMIT) h.past.shift();
      h.future = [];
      setHistTick(t => t + 1);
    }
    h.gesture = null;
  }, []);

  // ───────── عمليات العناصر ─────────
  const changeElement = useCallback((id, patch, live) => {
    const cur = layoutRef.current;
    const next = { ...cur, elements: cur.elements.map(e => (e.id === id ? { ...e, ...patch } : e)) };
    if (live) apply(next); else commit(next);
  }, [apply, commit]);

  const changeLayout = useCallback((next) => commit(next), [commit]);

  const setBandHeight = useCallback((band, h, live) => {
    const cur = layoutRef.current;
    const next = { ...cur, bands: { ...cur.bands, [band]: { ...cur.bands[band], height: h } } };
    if (live) apply(next); else commit(next);
  }, [apply, commit]);

  const addElement = useCallback((el) => {
    const cur = layoutRef.current;
    const maxZ = Math.max(0, ...cur.elements.map(e => e.z || 1));
    const W = contentWidth(cur);
    const bandH = cur.bands[el.band].height;
    const fixed = {
      ...el, z: maxZ + 1,
      w: Math.min(el.w, W),
      x: Math.max(0, Math.min(el.x, W - Math.min(el.w, W))),
      y: Math.max(0, Math.min(el.y, Math.max(0, bandH - el.h))),
    };
    const grow = fixed.y + fixed.h > bandH ? { [el.band]: { height: Math.ceil(fixed.y + fixed.h + 2) } } : {};
    commit({ ...cur, bands: { ...cur.bands, ...grow }, elements: [...cur.elements, fixed] });
    setSelectedId(fixed.id);
  }, [commit]);

  const onDropNew = useCallback((band, x, y, payload) => {
    if (payload.kind === "field") {
      const v = [...VARIABLE_GROUPS, customFieldsGroup(customFieldDefs)].flatMap(g => g.items).find(i => i.key === payload.key);
      if (v) addElement(fieldElement(band, x, y, v, payload.withLabel));
    } else {
      addElement(makeElement(payload.type, band, x, y));
    }
  }, [addElement, customFieldDefs]);

  // نقر على أداة = إضافتها في أعلى رأس الصفحة
  const quickAdd = (type) => {
    const cur = layoutRef.current;
    const n = cur.elements.filter(e => e.band === "header").length;
    addElement(makeElement(type, "header", 4 + (n % 6) * 3, 4 + (n % 6) * 3));
  };
  const quickAddField = (v) => {
    const cur = layoutRef.current;
    const n = cur.elements.filter(e => e.band === "header").length;
    addElement(fieldElement("header", 4 + (n % 6) * 3, 4 + (n % 6) * 3, v, withLabel));
  };

  const selectedEl = selectedId && selectedId !== "table" ? layout.elements.find(e => e.id === selectedId) : null;

  const deleteSelected = useCallback(() => {
    const cur = layoutRef.current;
    if (!selectedId || selectedId === "table") return;
    commit({ ...cur, elements: cur.elements.filter(e => e.id !== selectedId) });
    setSelectedId(null);
  }, [selectedId, commit]);

  const duplicateSelected = useCallback(() => {
    const cur = layoutRef.current;
    const el = cur.elements.find(e => e.id === selectedId);
    if (!el) return;
    addElement({ ...el, id: newId(), locked: false, x: el.x + 3, y: el.y + 3, props: { ...el.props } });
  }, [selectedId, addElement]);

  const arrange = useCallback((dir) => {
    const cur = layoutRef.current;
    const zs = cur.elements.map(e => e.z || 1);
    const z = dir === "front" ? Math.max(...zs) + 1 : Math.min(...zs) - 1;
    changeElement(selectedId, { z }, false);
  }, [selectedId, changeElement]);

  // ───────── التحميل ─────────
  useEffect(() => {
    if (!companyId) return;
    axios.get(`/InvoiceTypes?companyId=${companyId}`).then(res => {
      const list = Array.isArray(res.data) ? res.data : [];
      setInvoiceTypes(list);
      if (list.length && initialKind() === "invoice") setSelectedType(list[0].invoiceTypeId);
    }).catch(() => toast.error("تعذر تحميل أنواع الفواتير"));
    axios.get(`/CompanyProfile/public/${companyId}`).then(res => setCompany({ ...(res.data || {}), ...pick(mapCompany(res.data)) })).catch(() => {});
  }, [companyId]);

  const loadSet = useCallback((list) => {
    const set = list.length ? list : [kind === "voucher"
      ? { id: tplId(), name: "سند A4", isDefault: true, layout: templateForPaperOf("voucher", "A4") }
      : { id: tplId(), name: "فاتورة ضريبية شاملة", isDefault: true, layout: templateForPaper("A4") }];
    const d = defaultOf(set);
    savedJson.current = list.length ? serializeTemplateSet(set) : "";
    setTpls(set);
    setActiveId(d.id);
    reset(d.layout);
  }, [reset, kind]);

  useEffect(() => {
    if (!companyId || !selectedType) return;
    let alive = true;
    setLoading(true);
    printLayoutApi.get(companyId, selectedType)
      .then(res => {
        if (!alive) return;
        loadSet(parseTemplateSet(res?.data?.layoutJson));
      })
      .catch(() => { if (alive) loadSet([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [companyId, selectedType, loadSet]);

  const changeType = (v) => {
    if (dirty && !window.confirm("لديك تعديلات غير محفوظة. هل تريد المتابعة بدون حفظ؟")) return;
    setSelectedType(v);
  };

  // التبديل بين تصاميم الفواتير وتصاميم السندات
  const changeKind = (k) => {
    if (k === kind) return;
    if (dirty && !window.confirm("لديك تعديلات غير محفوظة. هل تريد المتابعة بدون حفظ؟")) return;
    setKind(k);
    setSelectedType(k === "voucher" ? VOUCHER_TYPE_LIST[0].invoiceTypeId : (invoiceTypes[0]?.invoiceTypeId ?? ""));
    setSelectedId(null);
  };

  // ───────── الحفظ ─────────
  const save = useCallback(async () => {
    if (!selectedType) { toast.error("يرجى اختيار نوع الفاتورة أولاً"); return; }
    setSaving(true);
    try {
      const set = tpls.map(t => (t.id === activeId ? { ...t, layout: layoutRef.current } : t));
      const json = serializeTemplateSet(set);
      await printLayoutApi.save({ companyId, invoiceTypeId: selectedType, layoutJson: json });
      savedJson.current = json;
      setTpls(set);
      setHistTick(t => t + 1);
      toast.success("تم حفظ تصميم الطباعة");
    } catch (err) {
      toast.error(err?.response?.data?.message || "فشل حفظ التصميم");
    } finally {
      setSaving(false);
    }
  }, [companyId, selectedType, tpls, activeId]);

  // ───────── إدارة القوالب ─────────
  const storeActive = () => tpls.map(t => (t.id === activeId ? { ...t, layout: layoutRef.current } : t));

  const switchTemplate = (id) => {
    if (id === activeId) return;
    const list = storeActive();
    const target = list.find(t => t.id === id);
    if (!target) return;
    setTpls(list);
    setActiveId(id);
    reset(target.layout);
  };

  const confirmTplDialog = () => {
    const d = tplDialog;
    const name = (d?.name || "").trim();
    if (!name) { toast.error("اكتب اسم القالب"); return; }
    if (d.mode === "rename") {
      setTpls(ts => ts.map(t => (t.id === activeId ? { ...t, name } : t)));
    } else {
      const base = d.base === "copy"
        ? JSON.parse(JSON.stringify(layoutRef.current))
        : (TEMPLATES.find(t => t.key === d.base) || TEMPLATES[0]).make();
      const nt = { id: tplId(), name, isDefault: false, layout: base };
      setTpls([...storeActive(), nt]);
      setActiveId(nt.id);
      reset(nt.layout);
    }
    setTplDialog(null);
  };

  const makeDefault = () => setTpls(ts => ts.map(t => ({ ...t, isDefault: t.id === activeId })));

  const deleteTemplate = () => {
    if (tpls.length <= 1) { toast.error("يجب أن يبقى قالب واحد على الأقل"); return; }
    const cur = tpls.find(t => t.id === activeId);
    if (!window.confirm(`حذف القالب "${cur?.name}"؟`)) return;
    let rest = tpls.filter(t => t.id !== activeId);
    if (cur?.isDefault) rest = rest.map((t, i) => ({ ...t, isDefault: i === 0 }));
    const next = defaultOf(rest);
    setTpls(rest);
    setActiveId(next.id);
    reset(next.layout);
  };

  // ───────── تصدير / استيراد التصاميم ─────────
  const [exportAnchor, setExportAnchor] = useState(null);
  const [importDlg, setImportDlg] = useState(null); // { file, items, strategy: 'add'|'replace', target: 'current'|'byType' }
  const [importing, setImporting] = useState(false);
  const importInput = useRef(null);
  const typeNameOf = (tid) => (isVoucherType(tid) ? VOUCHER_TYPE_LIST : invoiceTypes).find(t => t.invoiceTypeId === Number(tid))?.name || `نوع ${tid}`;

  const buildFile = (items) => ({
    format: FILE_TAG, version: 1, kind,
    exportedAt: new Date().toISOString(),
    source: { companyId, companyName: company?.name || company?.nameAr || "" },
    items,
  });

  const exportCurrent = () => {
    setExportAnchor(null);
    const t = currentSet.find(x => x.id === activeId);
    if (!t) return;
    downloadJson(buildFile([{ typeId: selectedType, typeName: typeNameOf(selectedType), templates: [stripTpl(t)] }]),
      `تصميم-${safeName(t.name)}.json`);
  };
  const exportType = () => {
    setExportAnchor(null);
    downloadJson(buildFile([{ typeId: selectedType, typeName: typeNameOf(selectedType), templates: currentSet.map(stripTpl) }]),
      `تصاميم-${safeName(typeNameOf(selectedType))}.json`);
  };
  const exportAll = async () => {
    setExportAnchor(null);
    setImporting(true);
    try {
      const items = [];
      for (const ty of types) {
        let list;
        if (ty.invoiceTypeId === selectedType) list = currentSet;
        else {
          const r = await printLayoutApi.get(companyId, ty.invoiceTypeId).catch(() => null);
          list = parseTemplateSet(r?.data?.layoutJson);
        }
        if (list.length) items.push({ typeId: ty.invoiceTypeId, typeName: ty.name, templates: list.map(stripTpl) });
      }
      if (!items.length) { toast.info("لا توجد تصاميم محفوظة للتصدير"); return; }
      downloadJson(buildFile(items), `نسخة-تصاميم-${kind === "voucher" ? "السندات" : "الفواتير"}-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success(`تم تصدير ${items.reduce((n, i) => n + i.templates.length, 0)} قالب لـ ${items.length} نوع`);
    } finally {
      setImporting(false);
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      // يقبل ملفنا، أو قالبًا واحدًا خامًا (layout v2) نُسخ من مكان آخر
      let items = data?.format === FILE_TAG && Array.isArray(data.items) ? data.items
        : data?.version === 2 && Array.isArray(data.elements) ? [{ typeId: selectedType, templates: [{ name: file.name.replace(/\.json$/i, ""), layout: data }] }]
        : null;
      if (!items) { toast.error("الملف ليس ملف تصاميم طباعة صالحًا"); return; }
      items = items
        .map(it => ({ ...it, templates: (it.templates || []).filter(t => t?.layout?.elements) }))
        .filter(it => it.templates.length);
      if (!items.length) { toast.error("الملف لا يحتوي على قوالب"); return; }
      setImportDlg({ fileName: file.name, source: data.source, exportedAt: data.exportedAt, items, strategy: "add", target: items.length > 1 ? "byType" : "current" });
    } catch {
      toast.error("تعذّر قراءة الملف — تأكد أنه ملف JSON صادر من مصمم الطباعة");
    }
  };

  // دمج قوالب مستوردة مع قائمة موجودة
  const mergeImported = (existing, imported, strategy) => {
    const fresh = imported.map(t => ({ id: tplId(), name: t.name || "قالب مستورد", isDefault: !!t.isDefault, layout: normalizeLayout(t.layout) }));
    let list;
    if (strategy === "replace") list = fresh;
    else {
      const names = new Set(existing.map(t => t.name));
      list = [...existing, ...fresh.map(t => ({ ...t, isDefault: false, name: names.has(t.name) ? `${t.name} (مستورد)` : t.name }))];
    }
    if (!list.some(t => t.isDefault) && list.length) list[0] = { ...list[0], isDefault: true };
    let seen = false; // افتراضي واحد فقط
    return list.map(t => { if (t.isDefault && !seen) { seen = true; return t; } return { ...t, isDefault: false }; });
  };

  const applyImport = async () => {
    const d = importDlg;
    if (!d) return;
    // 1) إلى النوع المفتوح حاليًا (في المصمم — ثم تضغط حفظ)
    if (d.target === "current") {
      const all = d.items.flatMap(it => it.templates);
      const list = mergeImported(storeActive(), all, d.strategy);
      const first = d.strategy === "replace" ? list[0] : list[list.length - all.length];
      setTpls(list);
      setActiveId(first.id);
      reset(first.layout);
      setImportDlg(null);
      toast.success(`تم استيراد ${all.length} قالب — راجعها ثم اضغط حفظ`);
      return;
    }
    // 2) كل نوع إلى نوعه (يُحفظ مباشرة في هذه الشركة)
    setImporting(true);
    let done = 0, skipped = 0;
    try {
      for (const it of d.items) {
        const tid = Number(it.typeId);
        const known = (isVoucherType(tid) ? VOUCHER_TYPE_LIST : invoiceTypes).some(t => t.invoiceTypeId === tid);
        if (!known) { skipped++; continue; }
        let existing;
        if (tid === selectedType) existing = storeActive();
        else {
          const r = await printLayoutApi.get(companyId, tid).catch(() => null);
          existing = parseTemplateSet(r?.data?.layoutJson);
        }
        const merged = mergeImported(existing, it.templates, d.strategy);
        await printLayoutApi.save({ companyId, invoiceTypeId: tid, layoutJson: serializeTemplateSet(merged) });
        if (tid === selectedType) loadSet(merged);
        done++;
      }
      toast.success(`تم الاستيراد والحفظ لـ ${done} نوع${skipped ? ` — تم تخطي ${skipped} نوع غير موجود في هذه الشركة` : ""}`);
      setImportDlg(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "فشل حفظ التصاميم المستوردة");
    } finally {
      setImporting(false);
    }
  };

  // ───────── لوحة المفاتيح ─────────
  useEffect(() => {
    const onKey = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (ctrl && k === "s") { e.preventDefault(); save(); return; }
      if (preview || isTyping(e.target) || editingId) return;
      if (ctrl && k === "z" && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if (ctrl && (k === "y" || (k === "z" && e.shiftKey))) { e.preventDefault(); redo(); return; }
      if (e.key === "Escape") { setSelectedId(null); return; }
      const el = layoutRef.current.elements.find(x => x.id === selectedId);
      if (!el) return;
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); deleteSelected(); return; }
      if (ctrl && k === "d") { e.preventDefault(); duplicateSelected(); return; }
      if (e.key === "Enter" && el.type === "text" && !el.locked) { e.preventDefault(); setEditingId(el.id); return; }
      const step = e.shiftKey ? 5 : 1;
      const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (d && !el.locked) {
        e.preventDefault();
        changeElement(el.id, { x: Math.round((el.x + d[0]) * 10) / 10, y: Math.max(0, Math.round((el.y + d[1]) * 10) / 10) }, false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save, undo, redo, deleteSelected, duplicateSelected, changeElement, selectedId, editingId, preview]);

  // تحذير قبل إغلاق الصفحة مع تعديلات غير محفوظة
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  // ───────── التكبير ─────────
  const fitZoom = useCallback(() => {
    const box = canvasBox.current;
    if (!box) return;
    const paperPx = (PAPER[layoutRef.current.page.size]?.w || 210) * (96 / 25.4);
    const z = Math.max(0.4, Math.min(2, (box.clientWidth - 48) / paperPx));
    setZoom(Math.round(z * 20) / 20);
  }, []);
  useEffect(() => { fitZoom(); }, [layout.page.size, fitZoom]);

  // ───────── القوالب والمقاس ─────────
  const applyTemplate = (tpl) => {
    setTplAnchor(null);
    commit(tpl.make());
    setSelectedId(null);
    toast.info("تم تطبيق القالب — يمكنك التراجع بـ Ctrl+Z");
  };
  const changePaper = (size) => {
    const cur = layoutRef.current;
    const wasThermal = PAPER[cur.page.size]?.thermal, toThermal = PAPER[size]?.thermal;
    if (wasThermal !== toThermal) {
      commit(templateForPaperOf(kind, size));
      toast.info("تم تحميل قالب مناسب للمقاس الجديد — Ctrl+Z للتراجع");
    } else {
      // نفس الفئة: نغيّر المقاس ونقلّص العناصر التي تخرج عن العرض
      const next = { ...cur, page: { ...cur.page, size } };
      const W = contentWidth(next);
      next.elements = cur.elements.map(e => {
        const w = Math.min(e.w, W);
        return { ...e, w, x: Math.max(0, Math.min(e.x, W - w)) };
      });
      commit(next);
    }
    setSelectedId(null);
  };

  // ───────── البيانات المعروضة ─────────
  useEffect(() => {
    const companyId = parseInt(localStorage.getItem("companyId"), 10);
    if (kind !== "invoice" || !selectedType || !companyId) { setCustomFieldDefs([]); return; }
    let alive = true;
    axios.get("/InvoiceFields", { params: { companyId, invoiceTypeId: selectedType } })
      .then(r => { if (alive) setCustomFieldDefs(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setCustomFieldDefs([]); });
    return () => { alive = false; };
  }, [kind, selectedType]);

  const previewData = useMemo(() => {
    const base = kind === "voucher" ? MOCK_VOUCHER : MOCK_DATA;
    const sample = { string: "نص تجريبي", decimal: "125.50", date: new Date().toLocaleDateString("en-GB"), bool: "نعم" };
    const customFields = customFieldDefs
      .filter(f => f.showInPrint !== false)
      .map(f => ({ key: f.fieldName, label: f.displayName, value: f.dataType === "list" ? (f.options?.[0] ?? "اختيار") : (sample[f.dataType] ?? "قيمة") }));
    return { ...base, customFields, company: { ...MOCK_DATA.company, ...company } };
  }, [company, kind, customFieldDefs]);
  const canvasData = useMemo(() => (showData ? previewData : { ...previewData, __raw: true }), [showData, previewData]);

  const handlePrint = useReactToPrint({
    contentRef: previewRef,
    content: () => previewRef.current,
    documentTitle: "معاينة تصميم الطباعة",
  });

  const groups = useMemo(() => {
    const q = fieldQuery.trim().toLowerCase();
    const kindGroups = kind === "invoice" && customFieldDefs.length
      ? [...variableGroupsFor(kind), customFieldsGroup(customFieldDefs)]
      : variableGroupsFor(kind);
    if (!q) return kindGroups;
    return kindGroups.map(g => ({ ...g, items: g.items.filter(i => i.label.toLowerCase().includes(q) || i.key.toLowerCase().includes(q)) }))
      .filter(g => g.items.length);
  }, [fieldQuery, kind, customFieldDefs]);

  const h = hist.current;
  const canUndo = h.past.length > 0, canRedo = h.future.length > 0;

  const typeName = types.find(t => t.invoiceTypeId === selectedType)?.name;

  // ======================================================================
  return (
    <Box dir="rtl" sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, md: 1.5 }, display: "flex", flexDirection: "column", height: { lg: "calc(100vh - 72px)" }, minHeight: 0 }}>
      <ReportHeader
        title="مصمم الطباعة"
        subtitle="اسحب العناصر والحقول إلى الصفحة، وانقر مرتين على أي نص لتحريره"
        icon={<DesignServicesIcon />}
        badges={[
          ...(typeName ? [{ label: typeName, icon: <PrintOutlinedIcon /> }] : []),
          { label: PAPER[layout.page.size]?.label || layout.page.size, icon: <StraightenIcon /> },
          ...(dirty ? [{ label: "تعديلات غير محفوظة", icon: <span style={{ width: 8, height: 8, borderRadius: 8, background: "#fbbf24", display: "inline-block" }} /> }] : []),
        ]}
        actions={[
          { label: "معاينة", icon: <VisibilityOutlinedIcon />, onClick: () => { setEditingId(null); setPreview(true); } },
          { label: saving ? "جارٍ الحفظ..." : "حفظ", icon: saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />, onClick: save, primary: true, disabled: saving || !selectedType },
        ]}
      />

      {/* ───── قوالب نوع الفاتورة ───── */}
      <Paper elevation={0} sx={{ ...panelSx, flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: .75, p: 1, mb: 1, overflow: "visible" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#475569", px: .5 }}>قوالب الطباعة:</Typography>
        {currentSet.map(t => {
          const on = t.id === activeId;
          return (
            <Box key={t.id} role="button" tabIndex={0} onClick={() => switchTemplate(t.id)} onKeyDown={e => { if (e.key === "Enter") switchTemplate(t.id); }}
              sx={{
                display: "inline-flex", alignItems: "center", gap: .6, px: 1.25, py: .6, borderRadius: "10px", cursor: "pointer", userSelect: "none",
                border: `1.5px solid ${on ? ACCENT : "#e5e7eb"}`, bgcolor: on ? `${ACCENT}12` : "#fff", color: on ? ACCENT : "#334155",
                "&:hover": { borderColor: ACCENT },
              }}>
              {t.isDefault && <StarIcon sx={{ fontSize: 16, color: "#f59e0b" }} />}
              <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{t.name}</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8" }}>{paperLabel(t.layout)}</Typography>
            </Box>
          );
        })}
        <Button size="small" startIcon={<AddIcon />} onClick={() => setTplDialog({ mode: "new", name: "", base: TEMPLATES[0].key })}
          sx={{ borderRadius: "10px", fontWeight: 700, "& .MuiButton-startIcon": { ml: .5, mr: -.25 } }}>
          قالب جديد
        </Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="حفظ التصميم في ملف على الجهاز">
          <Button size="small" variant="outlined" startIcon={importing ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />} disabled={importing}
            onClick={e => setExportAnchor(e.currentTarget)}
            sx={{ borderRadius: "10px", fontWeight: 700, color: "#334155", borderColor: "#e5e7eb", bgcolor: "#fff", "& .MuiButton-startIcon": { ml: .5, mr: -.25 } }}>
            تصدير
          </Button>
        </Tooltip>
        <Tooltip title="استيراد تصميم من ملف (من هذا الجهاز أو من شركة أخرى)">
          <Button size="small" variant="outlined" startIcon={<FileUploadOutlinedIcon />} disabled={importing} onClick={() => importInput.current?.click()}
            sx={{ borderRadius: "10px", fontWeight: 700, color: "#334155", borderColor: "#e5e7eb", bgcolor: "#fff", "& .MuiButton-startIcon": { ml: .5, mr: -.25 } }}>
            استيراد
          </Button>
        </Tooltip>
        <input ref={importInput} type="file" accept=".json,application/json" hidden onChange={onImportFile} />
        <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)} PaperProps={{ sx: { borderRadius: "12px", minWidth: 260 } }}>
          <MenuItem onClick={exportCurrent}>
            <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="القالب الحالي فقط" secondary={currentSet.find(t => t.id === activeId)?.name} />
          </MenuItem>
          <MenuItem onClick={exportType}>
            <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={`كل قوالب: ${typeName || ""}`} secondary={`${currentSet.length} قالب`} />
          </MenuItem>
          <Divider />
          <MenuItem onClick={exportAll}>
            <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={kind === "voucher" ? "نسخة كاملة: كل أنواع السندات" : "نسخة كاملة: كل أنواع الفواتير"} secondary="لنقل كل التصاميم لشركة أخرى" />
          </MenuItem>
        </Menu>
        <Divider orientation="vertical" flexItem sx={{ mx: .25 }} />
        <Tooltip title="تعيين كقالب افتراضي (يُطبع تلقائيًا)">
          <span><IconButton size="small" onClick={makeDefault} disabled={currentSet.find(t => t.id === activeId)?.isDefault}>
            {currentSet.find(t => t.id === activeId)?.isDefault ? <StarIcon sx={{ color: "#f59e0b" }} fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton></span>
        </Tooltip>
        <Tooltip title="إعادة تسمية"><IconButton size="small" onClick={() => setTplDialog({ mode: "rename", name: currentSet.find(t => t.id === activeId)?.name || "" })}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="حذف القالب"><span><IconButton size="small" onClick={deleteTemplate} disabled={tpls.length <= 1} sx={{ color: "#e11d48" }}><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
      </Paper>

      {/* ───── شريط الأدوات ───── */}
      <Paper elevation={0} sx={{ ...panelSx, flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 1, p: 1, mb: 1.25, overflow: "visible" }}>
        <ToggleButtonGroup size="small" exclusive value={kind} onChange={(_, v) => v && changeKind(v)}
          sx={{ "& .MuiToggleButton-root": { px: 1.75, fontWeight: 800, borderRadius: "10px !important", border: "1px solid #e5e7eb !important", mx: .25, height: 40 },
            "& .Mui-selected": { bgcolor: "#eef2ff !important", color: `${ACCENT} !important` } }}>
          <ToggleButton value="invoice">الفواتير</ToggleButton>
          <ToggleButton value="voucher">السندات</ToggleButton>
        </ToggleButtonGroup>
        <TextField select size="small" label={kind === "voucher" ? "نوع السند" : "نوع الفاتورة"} value={types.some(t => t.invoiceTypeId === selectedType) ? selectedType : ""} onChange={e => changeType(e.target.value)}
          sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
          {types.map(t => <MenuItem key={t.invoiceTypeId} value={t.invoiceTypeId}>{t.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="مقاس الورق" value={layout.page.size} onChange={e => changePaper(e.target.value)}
          sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
          {Object.entries(PAPER).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <Button variant="outlined" startIcon={<AutoAwesomeMosaicOutlinedIcon />} endIcon={<ExpandMoreIcon />} onClick={e => setTplAnchor(e.currentTarget)}
          sx={{ borderRadius: "10px", fontWeight: 700, height: 40, "& .MuiButton-startIcon": { ml: .75, mr: -.25 }, "& .MuiButton-endIcon": { mr: .5, ml: -.25 } }}>
          تصاميم جاهزة
        </Button>
        <Menu anchorEl={tplAnchor} open={!!tplAnchor} onClose={() => setTplAnchor(null)} PaperProps={{ sx: { borderRadius: "12px", minWidth: 220 } }}>
          {TEMPLATES.map(t => (
            <MenuItem key={t.key} onClick={() => applyTemplate(t)}>
              <ListItemIcon><AutoAwesomeMosaicOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={t.label} />
            </MenuItem>
          ))}
          <Divider />
          <MenuItem onClick={() => { setTplAnchor(null); commit({ ...layoutRef.current, elements: [] }); setSelectedId(null); }}>
            <ListItemIcon><RestartAltIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="صفحة فارغة" secondary="حذف كل العناصر" />
          </MenuItem>
        </Menu>

        <Divider orientation="vertical" flexItem sx={{ mx: .5 }} />
        <Tooltip title="تراجع (Ctrl+Z)"><span><IconButton onClick={undo} disabled={!canUndo}><UndoIcon /></IconButton></span></Tooltip>
        <Tooltip title="إعادة (Ctrl+Y)"><span><IconButton onClick={redo} disabled={!canRedo}><RedoIcon /></IconButton></span></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: .5 }} />
        <Tooltip title="تصغير"><IconButton onClick={() => setZoom(z => Math.max(0.4, Math.round((z - 0.1) * 10) / 10))}><ZoomOutIcon /></IconButton></Tooltip>
        <Typography sx={{ fontWeight: 800, fontSize: 13, minWidth: 44, textAlign: "center", direction: "ltr" }}>{Math.round(zoom * 100)}%</Typography>
        <Tooltip title="تكبير"><IconButton onClick={() => setZoom(z => Math.min(2, Math.round((z + 0.1) * 10) / 10))}><ZoomInIcon /></IconButton></Tooltip>
        <Tooltip title="ملاءمة العرض"><IconButton onClick={fitZoom}><FitScreenIcon /></IconButton></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: .5 }} />
        <Tooltip title={showGrid ? "إخفاء الشبكة" : "إظهار الشبكة"}>
          <IconButton onClick={() => setShowGrid(v => !v)} sx={{ color: showGrid ? ACCENT : undefined }}>{showGrid ? <GridOnIcon /> : <GridOffIcon />}</IconButton>
        </Tooltip>
        <Tooltip title={snap ? "الالتصاق بالملّيمتر: مفعّل" : "الالتصاق بالملّيمتر: متوقف"}>
          <IconButton onClick={() => setSnap(v => !v)} sx={{ color: snap ? ACCENT : undefined }}><StraightenIcon /></IconButton>
        </Tooltip>
        <Tooltip title={showData ? "عرض أسماء المتغيرات" : "عرض بيانات تجريبية"}>
          <Button size="small" onClick={() => setShowData(v => !v)} startIcon={showData ? <DatasetOutlinedIcon /> : <DataObjectIcon />}
            sx={{ borderRadius: "10px", fontWeight: 700, color: ACCENT, bgcolor: `${ACCENT}10`, "& .MuiButton-startIcon": { ml: .5, mr: -.25 } }}>
            {showData ? "بيانات تجريبية" : "المتغيرات"}
          </Button>
        </Tooltip>
        {loading && <CircularProgress size={20} sx={{ mr: "auto" }} />}
      </Paper>

      {/* ───── منطقة العمل ───── */}
      <Box sx={{
        flex: 1, minHeight: 0, display: "grid", gap: 1.25,
        gridTemplateColumns: { xs: "1fr", lg: "250px minmax(0,1fr) 340px" },
      }}>
        {/* صندوق الأدوات والحقول */}
        <Paper elevation={0} sx={{ ...panelSx, maxHeight: { xs: 380, lg: "none" } }}>
          <Box sx={{ p: 1.5, borderBottom: "1px solid #f1f5f9" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#64748b", mb: 1 }}>العناصر — اسحب أو انقر</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: .75 }}>
              {TOOLS.filter(t => !(kind === "voucher" && t.type === "totals")).map(t => (
                <Box
                  key={t.type} draggable role="button" tabIndex={0}
                  onDragStart={e => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ kind: "element", type: t.type })); e.dataTransfer.effectAllowed = "copy"; }}
                  onClick={() => quickAdd(t.type)}
                  onKeyDown={e => { if (e.key === "Enter") quickAdd(t.type); }}
                  sx={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: .25, py: 1, cursor: "grab",
                    borderRadius: "10px", border: "1px solid #e5e7eb", bgcolor: "#fafbfc", userSelect: "none",
                    "& svg": { fontSize: 20, color: ACCENT },
                    "&:hover": { borderColor: ACCENT, bgcolor: `${ACCENT}0d` },
                  }}
                >
                  {t.icon}
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>{t.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ p: 1.5, pb: 1, borderBottom: "1px solid #f1f5f9" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#64748b", mb: 1 }}>حقول البيانات</Typography>
            <TextField size="small" fullWidth placeholder="بحث عن حقل..." value={fieldQuery} onChange={e => setFieldQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 13 } }} />
            <FormControlLabel sx={{ m: 0, mt: .5 }} control={<Switch size="small" checked={withLabel} onChange={e => setWithLabel(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 12.5 }}>إضافة الحقل مع عنوانه</Typography>} />
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", px: 1, py: .5 }}>
            {groups.map(g => {
              const open = fieldQuery ? true : (openGroups[g.key] ?? g.key === "custom");
              return (
                <Box key={g.key} sx={{ mb: .5 }}>
                  <Box onClick={() => setOpenGroups(s => ({ ...s, [g.key]: !s[g.key] }))}
                    sx={{ display: "flex", alignItems: "center", px: .75, py: .6, cursor: "pointer", borderRadius: "8px", "&:hover": { bgcolor: "#f8fafc" } }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, flex: 1, color: "#1e293b" }}>{g.label}</Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: .5 }}>{g.items.length}</Typography>
                    <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8", transform: open ? "rotate(180deg)" : "none", transition: ".2s" }} />
                  </Box>
                  <Collapse in={open}>
                    {g.items.map(v => (
                      <Tooltip key={v.key} title={`{${v.key}}`} placement="left">
                        <Box
                          draggable
                          onDragStart={e => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ kind: "field", key: v.key, withLabel })); e.dataTransfer.effectAllowed = "copy"; }}
                          onClick={() => quickAddField(v)}
                          sx={{
                            display: "flex", alignItems: "center", gap: .75, px: 1, py: .55, mx: .25, my: .25, cursor: "grab", userSelect: "none",
                            borderRadius: "8px", border: "1px solid transparent", fontSize: 12.5, color: "#334155",
                            "&:hover": { borderColor: "#e0e7ff", bgcolor: "#eef2ff", color: ACCENT },
                          }}
                        >
                          <DataObjectIcon sx={{ fontSize: 15, color: "#a5b4fc" }} />
                          <span>{v.label}</span>
                        </Box>
                      </Tooltip>
                    ))}
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* الصفحة */}
        <Box
          ref={canvasBox}
          onPointerDown={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
          sx={{
            borderRadius: "14px", border: "1px solid #e5e7eb", overflow: "auto", minHeight: { xs: 500, lg: 0 },
            bgcolor: "#eef0f4", backgroundImage: "radial-gradient(#d6d9e0 1px, transparent 1px)", backgroundSize: "16px 16px",
            p: 3, position: "relative",
          }}
        >
            <DesignerCanvas
              layout={layout} data={canvasData} zoom={zoom} showGrid={showGrid} snap={snap}
              selectedId={selectedId} editingId={editingId}
              onSelect={(id) => { setSelectedId(id); if (id !== editingId) setEditingId(null); }}
              onEdit={setEditingId}
              onChangeElement={changeElement}
              onGestureStart={gestureStart} onGestureEnd={gestureEnd}
              onDropNew={onDropNew} onBandHeight={setBandHeight}
            />
        </Box>

        {/* الخصائص */}
        <Paper elevation={0} sx={{ ...panelSx }}>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <PropertiesPanel
              layout={layout}
              selected={selectedId === "table" ? "table" : selectedEl}
              onChangeElement={(id, patch) => changeElement(id, patch, false)}
              onDelete={deleteSelected}
              onDuplicate={duplicateSelected}
              onArrange={arrange}
              onChangeLayout={changeLayout}
            />
          </Box>
        </Paper>
      </Box>

      {/* ───── استيراد تصاميم ───── */}
      <Dialog open={!!importDlg} onClose={() => !importing && setImportDlg(null)} dir="rtl" PaperProps={{ sx: { borderRadius: "16px", width: 520, maxWidth: "94vw" } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>استيراد تصاميم الطباعة</DialogTitle>
        {importDlg && (
          <DialogContent sx={{ display: "grid", gap: 1.5 }}>
            <Box sx={{ p: 1.5, borderRadius: "12px", bgcolor: "#f8fafc", border: "1px solid #e5e7eb", fontSize: 13, lineHeight: 1.9 }}>
              <div><b>الملف:</b> {importDlg.fileName}</div>
              {importDlg.source?.companyName && <div><b>من شركة:</b> {importDlg.source.companyName}</div>}
              {importDlg.exportedAt && <div><b>تاريخ التصدير:</b> {new Date(importDlg.exportedAt).toLocaleString("en-GB")}</div>}
              <Box sx={{ mt: .5 }}>
                {importDlg.items.map(it => (
                  <div key={it.typeId}>• {it.typeName || typeNameOf(it.typeId)}: {it.templates.map(t => t.name).join("، ")}</div>
                ))}
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#475569", mb: .75 }}>استيراد إلى</Typography>
              <ToggleButtonGroup exclusive fullWidth size="small" value={importDlg.target} onChange={(_, v) => v && setImportDlg(d => ({ ...d, target: v }))}
                sx={{ "& .MuiToggleButton-root": { fontWeight: 700, borderRadius: "10px !important", border: "1px solid #e5e7eb !important", mx: .25 },
                  "& .Mui-selected": { bgcolor: "#eef2ff !important", color: `${ACCENT} !important` } }}>
                <ToggleButton value="current">النوع المفتوح: {typeName}</ToggleButton>
                <ToggleButton value="byType">كل نوع إلى نوعه</ToggleButton>
              </ToggleButtonGroup>
              <Typography sx={{ fontSize: 12, color: "#64748b", mt: .75, lineHeight: 1.7 }}>
                {importDlg.target === "current"
                  ? "تُضاف القوالب للنوع المفتوح في المصمم لتراجعها، ثم تضغط حفظ."
                  : "تُحفظ مباشرة في هذه الشركة، كل قالب لنفس نوع المستند الذي صُدّر منه."}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#475569", mb: .75 }}>القوالب الموجودة</Typography>
              <ToggleButtonGroup exclusive fullWidth size="small" value={importDlg.strategy} onChange={(_, v) => v && setImportDlg(d => ({ ...d, strategy: v }))}
                sx={{ "& .MuiToggleButton-root": { fontWeight: 700, borderRadius: "10px !important", border: "1px solid #e5e7eb !important", mx: .25 },
                  "& .Mui-selected": { bgcolor: "#eef2ff !important", color: `${ACCENT} !important` } }}>
                <ToggleButton value="add">إضافة بجانبها</ToggleButton>
                <ToggleButton value="replace">استبدالها</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
              بيانات المنشأة والشعار تُقرأ دائمًا من الشركة الحالية — التصميم ينقل الشكل فقط.
            </Typography>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImportDlg(null)} disabled={importing} sx={{ borderRadius: "10px", fontWeight: 700 }}>إلغاء</Button>
          <Button variant="contained" onClick={applyImport} disabled={importing}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <FileUploadOutlinedIcon />}
            sx={{ borderRadius: "10px", fontWeight: 700, bgcolor: ACCENT, "& .MuiButton-startIcon": { ml: .75, mr: -.25 } }}>
            استيراد
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───── قالب جديد / إعادة تسمية ───── */}
      <Dialog open={!!tplDialog} onClose={() => setTplDialog(null)} dir="rtl" PaperProps={{ sx: { borderRadius: "16px", width: 420, maxWidth: "94vw" } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{tplDialog?.mode === "rename" ? "إعادة تسمية القالب" : "قالب طباعة جديد"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
          <TextField autoFocus label="اسم القالب" placeholder="مثال: فاتورة A4 بالشعار، إيصال حراري..." value={tplDialog?.name || ""}
            onChange={e => setTplDialog(d => ({ ...d, name: e.target.value }))}
            onKeyDown={e => { if (e.key === "Enter") confirmTplDialog(); }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          {tplDialog?.mode === "new" && (
            <TextField select label="يبدأ من" value={tplDialog.base} onChange={e => setTplDialog(d => ({ ...d, base: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
              {TEMPLATES.map(t => <MenuItem key={t.key} value={t.key}>تصميم جاهز — {t.label}</MenuItem>)}
              <MenuItem value="copy">نسخة من القالب الحالي</MenuItem>
            </TextField>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTplDialog(null)} sx={{ borderRadius: "10px", fontWeight: 700 }}>إلغاء</Button>
          <Button variant="contained" onClick={confirmTplDialog} sx={{ borderRadius: "10px", fontWeight: 700, bgcolor: ACCENT }}>
            {tplDialog?.mode === "rename" ? "حفظ الاسم" : "إنشاء"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───── المعاينة ───── */}
      <Dialog open={preview} onClose={() => setPreview(false)} maxWidth={false}
        PaperProps={{ sx: { borderRadius: "16px", bgcolor: "#eef0f4", maxHeight: "94vh" } }}>
        <DialogContent sx={{ p: 3, display: "flex", justifyContent: "center" }}>
          <Box sx={{ boxShadow: "0 10px 30px -10px rgba(15,23,42,.35)" }}>
            <PrintLayoutRenderer ref={previewRef} layout={layout} data={previewData} />
          </Box>
        </DialogContent>
        <DialogActions dir="rtl" sx={{ px: 3, pb: 2, bgcolor: "#fff", gap: 1 }}>
          <Typography sx={{ fontSize: 12.5, color: "#64748b", flex: 1 }}>معاينة ببيانات تجريبية وبيانات منشأتك</Typography>
          <Button onClick={() => setPreview(false)} sx={{ borderRadius: "10px", fontWeight: 700 }}>إغلاق</Button>
          <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={handlePrint}
            sx={{ borderRadius: "10px", fontWeight: 700, bgcolor: ACCENT, "& .MuiButton-startIcon": { ml: .75, mr: -.25 } }}>
            طباعة تجريبية
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}